import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import './CartDrawer.css';

export default function CartDrawer() {
  const {
    cart,
    cartOpen,
    setCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartCount,
    checkout,
    minecraftUsername,
  } = useStore();

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setCartOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    if (!minecraftUsername) {
      setCheckoutError('Please enter your Minecraft username on the shop page first');
      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const basketData = await checkout(minecraftUsername);

      // Use Tebex.js for embedded checkout
      if (window.Tebex && basketData?.ident) {
        window.Tebex.checkout.init({
          ident: basketData.ident,
          theme: 'dark',
        });
        window.Tebex.checkout.launch();

        // Listen for checkout completion
        window.Tebex.checkout.on('payment:complete', () => {
          clearCart();
          handleClose();
          navigate('/shop/success');
        });
      } else if (basketData?.links?.checkout) {
        // Fallback to redirect if Tebex.js not available
        window.location.href = basketData.links.checkout;
      } else {
        throw new Error('No checkout URL available');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  if (!cartOpen) return null;

  return createPortal(
    <div className={`cart-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`cart-drawer ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cart-header">
          <h2 className="cart-title">
            <i className="mdi mdi-cart"></i>
            Your Cart
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </h2>
          <button className="cart-close" onClick={handleClose}>
            <i className="mdi mdi-close"></i>
          </button>
        </div>

        <div className="cart-content">
          {cart.length === 0 ? (
            <div className="cart-empty">
              <i className="mdi mdi-cart-outline"></i>
              <p>Your cart is empty</p>
              <button className="btn-continue-shopping" onClick={handleClose}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="cart-items">
              {cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <img
                    src={item.image || '/logo.png'}
                    alt={item.name}
                    className="cart-item-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/logo.png';
                    }}
                  />

                  <div className="cart-item-info">
                    <h4 className="cart-item-name">{item.name}</h4>
                    <span className="cart-item-price">
                      ${formatPrice(item.base_price)}
                    </span>
                  </div>

                  <div className="cart-item-quantity">
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    >
                      <i className="mdi mdi-minus"></i>
                    </button>
                    <span className="qty-value">{item.quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      <i className="mdi mdi-plus"></i>
                    </button>
                  </div>

                  <button
                    className="cart-item-remove"
                    onClick={() => removeFromCart(item.id)}
                  >
                    <i className="mdi mdi-trash-can-outline"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="cart-footer">
            {minecraftUsername && (
              <div className="cart-user-info">
                <img
                  src={`https://mc-heads.net/avatar/${minecraftUsername}/24`}
                  alt={minecraftUsername}
                  className="cart-user-avatar"
                />
                <span>Purchasing as <strong>{minecraftUsername}</strong></span>
              </div>
            )}

            <div className="cart-total">
              <span>Total</span>
              <span className="cart-total-amount">${formatPrice(cartTotal)}</span>
            </div>

            {checkoutError && (
              <p className="checkout-error">{checkoutError}</p>
            )}

            <button
              className="btn-checkout"
              onClick={handleCheckout}
              disabled={checkoutLoading || !minecraftUsername}
            >
              {checkoutLoading ? (
                <>
                  <span className="checkout-spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="mdi mdi-lock"></i>
                  Checkout
                </>
              )}
            </button>

            <button className="btn-clear-cart" onClick={clearCart}>
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
